import { Fragment, PropsWithChildren, RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import throttle from 'lodash/throttle';

import { ChatMessage } from '@r1-frontend/ui-react/components/ChatMessage';
import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { PromoParagraph0 } from '~/src/features/internetV2/components/typography';
import { setSectionVisibility } from '~/src/features/internetV2/store/actions';
import { selectSections } from '~/src/features/internetV2/store/selectors';
import { useValueByBreakpoint } from '~/src/helpers/hooks/useValueByBreakpoint';
import { TState } from '~/src/store';
import IncomingAvatar from '~/static/images/internet/v2/chat/in-avatar.png';
import IncomingAvatarWebp from '~/static/images/internet/v2/chat/in-avatar.webp';
import OutgoingAvatar from '~/static/images/internet/v2/chat/out-avatar.png';
import OutgoingAvatarWebp from '~/static/images/internet/v2/chat/out-avatar.webp';

import * as ST from './styled';

type TSectionProps = PropsWithChildren<{
    chat?: {
        in: React.ReactNode,
        out: React.ReactNode,
    },
    wide?: boolean,
    background?: COLORS,
    root?: RefObject<HTMLElement>,
    $key?: string,
}>;

const Section = ({
    chat,
    wide,
    background,

    children,
    root,
    $key = '',
}: TSectionProps) => {
    const [top, setTop] = useState(0);
    const [height, setHeight] = useState(0);

    const ref = useRef<HTMLElement | null>(null);
    const { isLoading, sections, keys } = useSelector((state: TState) => {
        const { sections, keys } = selectSections(state);

        return {
            isLoading: state.internetReducer.isLoading,
            sections,
            keys,
        };
    });

    const Wrapper = wide ? Fragment : WideContainer;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const { clientHeight } = ref.current;

        setHeight(clientHeight);
        setTop(
            clientHeight > window.innerHeight
                ? window.innerHeight - clientHeight
                : 0,
        );
    }, [ref.current?.clientHeight, isLoading]);

    useEffect(() => {
        const container = root?.current;

        const onScroll = throttle(() => {
            const index = keys.indexOf($key);

            if (~index && ref.current) {
                const { bottom } = ref.current.getBoundingClientRect();
                const boundingOffset = 32;
                const isSectionFullyOnScreen = bottom <= window.innerHeight + boundingOffset;

                dispatch(setSectionVisibility({
                    key: keys[index - 1],
                    visible: !isSectionFullyOnScreen,
                }));
            }
        }, 200);

        container && container.addEventListener('scroll', onScroll, false);

        return () => {
            container && container.removeEventListener('scroll', onScroll, false);
        };
    }, [root, top, keys, $key]);

    const chatMaxWidth = useValueByBreakpoint({
        desktop: 640,
        laptop: 400,
        mobile: 285,
    });

    if (!sections[$key]?.visible) {
        return <div style={{ height }} />;
    }

    return (
        <ST.Section
            ref={ref}
            $top={top}
            $background={background}
        >
            <Wrapper>
                <FullWidthListContainer
                    $direction={'column'}
                    $wrap={'nowrap'}
                    $size={0}
                >
                    {chat && (
                        <ST.MessagesContainer
                            $direction={'column'}
                            $desktop={64}
                            $mobile={24}
                        >
                            <ChatMessage
                                type={'out'}
                                maxWidth={chatMaxWidth}
                                avatar={
                                    <WebpImage urlWebp={OutgoingAvatarWebp}>
                                        <ST.Image src={OutgoingAvatar}/>
                                    </WebpImage>
                                }
                            >
                                <PromoParagraph0>{chat.out}</PromoParagraph0>
                            </ChatMessage>
                            <ChatMessage
                                type={'in'}
                                avatarBackground={'transparent'}
                                maxWidth={chatMaxWidth}
                                avatar={
                                    <WebpImage urlWebp={IncomingAvatarWebp}>
                                        <ST.Image src={IncomingAvatar}/>
                                    </WebpImage>
                                }
                            >
                                <PromoParagraph0>{chat.in}</PromoParagraph0>
                            </ChatMessage>
                        </ST.MessagesContainer>
                    )}

                    {children}
                </FullWidthListContainer>
            </Wrapper>
        </ST.Section>
    );
};

export default Section;
